/**
 * Result of the current estimate with Save / PDF / Compare actions
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Grid, Snackbar, Alert, Stack } from '@mui/material';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import BookmarkAddedRoundedIcon from '@mui/icons-material/BookmarkAddedRounded';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import { EmptyState, Page, PageHeader } from '../ui';
import EstimateResultView, { describeInputs } from './EstimateResultView';
import { pickBasics } from './QuickEstimatePage';
import { getEstimateCopy } from './copy';
import { useLanguage } from '../../utils/LanguageContext';
import { useEstimate } from '../../contexts/EstimateContext';
import { useGate } from '../../hooks/useGate';
import { GATE_ACTIONS } from '../../constants/gateConfig';
import { saveProject } from '../../utils/projectStorage';

export const ModeChip = ({ mode, t }) => (
  <Chip
    size="small"
    label={mode === 'quick' ? t.quickBadge : t.detailedBadge}
    color={mode === 'quick' ? 'default' : 'primary'}
    variant="outlined"
  />
);

const EstimateResultPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = getEstimateCopy(language);
  const { result, compare, setCompare, updateResult, setQuickDraft } = useEstimate();
  const { checkAccess, GateModalComponent } = useGate();
  const [toast, setToast] = useState(false);

  if (!result) {
    return (
      <Page>
        <EmptyState
          icon={<CalculateOutlinedIcon />}
          title={t.noResult}
          description={t.noResultDesc}
          action={
            <Button variant="contained" onClick={() => navigate('/app/estimate/quick')}>
              {t.startQuick}
            </Button>
          }
        />
      </Page>
    );
  }

  const isQuick = result.mode === 'quick';

  const handleSave = () => {
    if (!checkAccess(GATE_ACTIONS.SAVE_PROJECT)) return;
    const saved = saveProject(result);
    updateResult({ id: saved.id, savedAt: saved.savedAt });
    setToast(true);
  };

  const handlePdf = () => {
    if (!checkAccess(GATE_ACTIONS.EXPORT_PDF)) return;
    // Load the PDF library only when needed
    import('../../utils/pdfExport').then(({ generateEstimationPDF }) => generateEstimationPDF(result, language));
  };

  const handleCompare = () => {
    if (!checkAccess(GATE_ACTIONS.COMPARE_SCENARIOS)) return;
    navigate(`/app/estimate/${isQuick ? 'quick' : 'detailed'}?compare=1`);
  };

  const handleEdit = () => {
    if (isQuick) {
      setQuickDraft(pickBasics(result.inputs));
      navigate('/app/estimate/quick');
    } else {
      navigate('/app/estimate/detailed?from=result');
    }
  };

  return (
    <Page>
      <PageHeader
        overline={<ModeChip mode={result.mode} t={t} />}
        title={result.inputs.projectName || t.resultTitle}
        subtitle={describeInputs(result.inputs, t, language)}
        onBack={() => navigate('/app')}
        backLabel={t.back}
      />

      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <Button
            fullWidth
            variant={result.id ? 'outlined' : 'contained'}
            color={result.id ? 'success' : 'primary'}
            startIcon={result.id ? <BookmarkAddedRoundedIcon /> : <BookmarkBorderRoundedIcon />}
            onClick={result.id ? () => navigate(`/app/projects/${result.id}`) : handleSave}
          >
            {result.id ? t.saved : t.save}
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button fullWidth variant="outlined" startIcon={<PictureAsPdfOutlinedIcon />} onClick={handlePdf}>
            PDF
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button fullWidth variant="outlined" startIcon={<CompareArrowsRoundedIcon />} onClick={handleCompare}>
            {t.compare}
          </Button>
        </Grid>
      </Grid>

      <EstimateResultView
        estimate={result}
        compare={compare}
        onClearCompare={() => setCompare(null)}
        onRefine={isQuick ? () => navigate('/app/estimate/detailed?from=result') : undefined}
        t={t}
        language={language}
      />

      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
        <Button startIcon={<EditOutlinedIcon />} onClick={handleEdit}>
          {t.edit}
        </Button>
        <Button startIcon={<AddRoundedIcon />} onClick={() => navigate('/app/estimate')}>
          {t.newEstimate}
        </Button>
      </Stack>

      <Box sx={{ height: 8 }} />

      <Snackbar
        open={toast}
        autoHideDuration={4000}
        onClose={() => setToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 80, md: 24 } }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setToast(false)}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/app/projects')}>
              {language === 'my' ? 'Lihat' : 'View'}
            </Button>
          }
        >
          {t.savedToast}
        </Alert>
      </Snackbar>

      <GateModalComponent />
    </Page>
  );
};

export default EstimateResultPage;
